import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { getStroke } from 'perfect-freehand';
import { distance, type Point } from '../../doc/geometry';
import type { StrokeItem } from '../../doc/schema';
import { useBoard, usePane, usePaneStore, useReadOnly } from '../context';
import { outlineToPath, strokeOptions } from '../items/StrokeItem';
import { toolSettings } from '../toolSettings';
import { screenToWorld, worldToScreen } from './camera';

/** Time after a pen touch during which finger input is treated as a resting palm. */
export const PALM_WINDOW_MS = 1500;
let lastPenContact = 0;
export const notePenContact = (): void => {
  lastPenContact = Date.now();
};
export const penRecentlyUsed = (): boolean => Date.now() - lastPenContact < PALM_WINDOW_MS;

/** Should this pointer be ignored as a palm (or per the pen-only setting)? */
export function isRejectedTouch(pointerType: string): boolean {
  if (pointerType === 'pen') {
    notePenContact();
    return false;
  }
  if (pointerType !== 'touch') return false;
  return toolSettings.getState().penOnly || penRecentlyUsed();
}

/**
 * Screen-space canvas that captures ink while the draw tool is active. The stroke is drawn
 * immediately on the canvas (no React on the hot path); on release it becomes a stroke item.
 * Pointers it does not take (middle button, space, rejected touches) fall through to the
 * viewport, which pans.
 */
export function DrawOverlay() {
  const active = usePane((s) => s.tool === 'draw');
  const readOnly = useReadOnly();
  if (!active || readOnly) return null;
  return <DrawCanvas />;
}

interface Live {
  pointerId: number;
  pointerType: string;
  /** World-space points with pressure. */
  points: [number, number, number][];
  realPressure: boolean;
  erased: Set<string>;
}

function DrawCanvas() {
  const board = useBoard();
  const pane = usePaneStore();
  const size = usePane((s) => s.size);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<Live | null>(null);
  const dpr = window.devicePixelRatio || 1;

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = Math.round(size.w * dpr);
    c.height = Math.round(size.h * dpr);
  }, [size, dpr]);

  // Safari still runs its touch defaults (text selection, callouts, synthesised clicks on the
  // UI underneath) for pointers we ignore. Cancel them at the touch level, non-passively.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const swallow = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const opts: AddEventListenerOptions = { passive: false };
    c.addEventListener('touchstart', swallow, opts);
    c.addEventListener('touchmove', swallow, opts);
    c.addEventListener('touchend', swallow, opts);
    return () => {
      c.removeEventListener('touchstart', swallow);
      c.removeEventListener('touchmove', swallow);
      c.removeEventListener('touchend', swallow);
    };
  }, []);

  const toLocal = (e: { clientX: number; clientY: number }): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const render = () => {
    const c = canvasRef.current;
    const l = live.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    if (!l || !l.points.length) return;
    const ts = toolSettings.getState();
    const cam = pane.getState().camera;
    if (ts.penMode === 'eraser') {
      const last = l.points[l.points.length - 1]!;
      const p = worldToScreen(cam, { x: last[0], y: last[1] });
      ctx.beginPath();
      ctx.arc(p.x, p.y, (ts.eraserSize / 2) * cam.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(180,40,40,0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      return;
    }
    const tool = ts.penMode;
    const sizeWorld = (tool === 'pen' ? ts.penSize : ts.highlighterSize) / cam.zoom;
    const screenPts = l.points.map(([x, y, p]) => {
      const s = worldToScreen(cam, { x, y });
      return [s.x, s.y, p] as [number, number, number];
    });
    const outline = getStroke(screenPts, strokeOptions(sizeWorld * cam.zoom, tool, l.realPressure));
    const path = new Path2D(outlineToPath(outline));
    ctx.globalAlpha = tool === 'highlighter' ? 0.45 : 1;
    ctx.fillStyle = tool === 'pen' ? ts.penColor : ts.highlighterColor;
    ctx.fill(path);
    ctx.globalAlpha = 1;
  };

  const eraseAt = (world: Point, radiusWorld: number, erased: Set<string>) => {
    const hits = board.index.query({ x: world.x - radiusWorld, y: world.y - radiusWorld, w: radiusWorld * 2, h: radiusWorld * 2 });
    const toDelete: string[] = [];
    for (const item of hits) {
      if (item.type !== 'stroke' || board.isLocked(item) || board.isHidden(item) || erased.has(item.id)) continue;
      if (strokeHit(item, world, radiusWorld)) toDelete.push(item.id);
    }
    if (toDelete.length) {
      for (const id of toDelete) erased.add(id);
      board.deleteItems(toDelete);
    }
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (live.current) return;
    const s = pane.getState();
    if (e.button !== 0 && e.pointerType !== 'touch') return; // middle/right: fall through (pan)
    if (s.spaceHeld) return;
    if (isRejectedTouch(e.pointerType)) return; // palm or pen-only: viewport handles (two-finger pan)
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const world = screenToWorld(s.camera, toLocal(e));
    const realPressure = e.pointerType === 'pen';
    live.current = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      points: [[world.x, world.y, realPressure ? e.pressure : 0.5]],
      realPressure,
      erased: new Set(),
    };
    if (toolSettings.getState().penMode === 'eraser') {
      eraseAt(world, toolSettings.getState().eraserSize / 2 / s.camera.zoom, live.current.erased);
    }
    render();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (!l || l.pointerId !== e.pointerId) return;
    const s = pane.getState();
    const native = e.nativeEvent;
    const events = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const list = events.length ? events : [native];
    for (const ev of list) {
      const world = screenToWorld(s.camera, toLocal(ev));
      const last = l.points[l.points.length - 1]!;
      if (distance({ x: last[0], y: last[1] }, world) * s.camera.zoom < 0.7) continue;
      l.points.push([world.x, world.y, l.realPressure ? ev.pressure : 0.5]);
      if (toolSettings.getState().penMode === 'eraser') {
        eraseAt(world, toolSettings.getState().eraserSize / 2 / s.camera.zoom, l.erased);
      }
    }
    render();
  };

  const finish = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const l = live.current;
    if (!l || l.pointerId !== e.pointerId) return;
    live.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    render();
    const ts = toolSettings.getState();
    if (ts.penMode === 'eraser') return;
    const cam = pane.getState().camera;
    const tool = ts.penMode;
    const sizeWorld = (tool === 'pen' ? ts.penSize : ts.highlighterSize) / cam.zoom;
    const pad = sizeWorld;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of l.points) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const x = minX - pad;
    const y = minY - pad;
    const w = Math.max(1, maxX - minX + pad * 2);
    const h = Math.max(1, maxY - minY + pad * 2);
    const flat: number[] = [];
    for (const [px, py, p] of l.points) flat.push(round(px - x), round(py - y), round(p));
    board.createItem({
      type: 'stroke',
      x: round(x),
      y: round(y),
      w: round(w),
      h: round(h),
      props: {
        tool,
        color: tool === 'pen' ? ts.penColor : ts.highlighterColor,
        size: round(sizeWorld),
        points: flat,
        w0: round(w),
        h0: round(h),
        pressure: l.realPressure,
      },
    });
  };

  return (
    <canvas
      ref={canvasRef}
      className="draw-overlay"
      style={{ width: size.w, height: size.h }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
    />
  );
}

const round = (v: number) => Math.round(v * 100) / 100;

/** Is the world point within `radius` of any point of the stroke? Ignores rotation. */
function strokeHit(item: StrokeItem, world: Point, radius: number): boolean {
  const { points, w0, h0 } = item.props;
  const sx = item.w / Math.max(1, w0);
  const sy = item.h / Math.max(1, h0);
  const r2 = (radius + item.props.size / 2) ** 2;
  for (let i = 0; i + 1 < points.length; i += 3) {
    const px = item.x + points[i]! * sx;
    const py = item.y + points[i + 1]! * sy;
    const dx = px - world.x;
    const dy = py - world.y;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}
